import { MutableRefObject, useContext, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";

import {
  Button,
  Flex,
  Form,
  FormErrorTypes,
  Text,
  ViewerContext,
  toast,
  DropdownOptions,
  TooltipPopup,
  Spinner,
} from "dash-ui";
import { SubscriptionProductModel } from "dash-common";
import { useNavigate } from "react-router-dom";

import {
  GetCurrentAccountProductSubscriptionDocument,
  GetCurrentBrokerDocument,
  useCanUserTrialForProductQuery,
  useChangeCurrentProductSubscriptionMutation,
  useCreateProductSubscriptionMutation,
} from "gql/generated.graphql";

import { RoutesPath } from "flow-components/routes/routes";
import { ProfileTabsKeys } from "pages/ProfilePage/ProfilePage.constants";
import { usePaymentConfirmation } from "common/hooks/use-payment-confirmation/use-payment-confirmation";
import SubscriptionsCard from "pages/authentication/complete-sign-up-scope/AddPaymentPage/components/SubscriptionsCard/SubscriptionsCard";
import OrderSummaryCard from "pages/authentication/complete-sign-up-scope/AddPaymentPage/components/OrderSummaryCard/OrderSummaryCard";
import { PaymentMethodsSection } from "pages/ProfilePage/components/ProfileBilling/components";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { getChangeType } from "common/helpers";

const HeaderContainer = styled(Flex)`
  margin-top: ${({ theme }) => theme.space * 10}px;
  @media ${({ theme }) => theme.devices.mobile} {
    margin-top: ${({ theme }) => theme.space * 4}px;
  }
`;
const Card = styled(Flex)`
  border-radius: ${({ theme }) => theme.space * 2}px;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  background-color: ${({ theme }) => theme.colors.white};
`;

/**
 * deprecated
 */
function AddPaymentPage() {
  const { account, userHasTrialed, subscription } = useContext(ViewerContext);
  const { t } = useTranslation(["pages", "common"]);
  useBrowserTabTitle(`Subscriptions ${t("common:labels.dashSuffix")}`);
  const theme = useTheme();
  const navigate = useNavigate();
  const ref = useRef<MutableRefObject<HTMLFormElement | null>>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<SubscriptionProductModel | null>(null);
  const [coupon, setCoupon] = useState<{ coupon: any; isValid: boolean }>({
    coupon: null,
    isValid: false,
  });

  const [changeProductSubscription, { loading: isChangeSubscriptionLoading }] =
    useChangeCurrentProductSubscriptionMutation();

  const [createProductSubscription, { loading: isCreateSubscriptionLoading }] =
    useCreateProductSubscriptionMutation({
      refetchQueries: [
        GetCurrentAccountProductSubscriptionDocument,
        GetCurrentBrokerDocument,
      ],
    });

  const { data: canUserTrialForProductData, loading: loadingTrialData } =
    useCanUserTrialForProductQuery({
      skip: !selectedProduct?.stripeProductId,
      variables: {
        input: {
          stripeProductId: selectedProduct?.stripeProductId || "",
        },
      },
    });

  const [paymentConfirmation, { stripeLoading }] = usePaymentConfirmation({
    onSuccess: () => {
      toast.success(
        t("pages:completeSignUpAddPaymentPage:subscriptionStarted")
      );

      navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`);
    },
    onError: () =>
      navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`),
  });
  const [errorSlug] = useState("");

  const userCanTrial = canUserTrialForProductData?.canUserTrialForProduct;

  const changeType =
    selectedProduct &&
    getChangeType(subscription?.subscriptionProduct, selectedProduct);

  const loadingPlans = !selectedProduct;

  const onSubmit = () => {
    if (selectedProduct) {
      if (userCanTrial && selectedProduct.trial) {
        createProductSubscription({
          variables: {
            input: {
              stripePaymentMethodId: account?.stripeDefaultPaymentMethodId,
              subscriptionProductId: selectedProduct._id,
              stripeCouponId: coupon.coupon?.stripeCouponId,
            },
          },
        })
          .then(({ data }) =>
            paymentConfirmation(data?.createProductSubscription)
          )
          .catch((error) => toast.error(error.toString()));
      } else {
        changeProductSubscription({
          variables: {
            input: {
              stripePaymentMethodId: account?.stripeDefaultPaymentMethodId,
              subscriptionProductId: selectedProduct._id,
              stripeCouponId: coupon.coupon?.stripeCouponId,
            },
          },
        })
          .then(({ data }) =>
            paymentConfirmation(data?.changeCurrentProductSubscription)
          )
          .catch((error) => toast.error(error.toString()));
      }
    }
  };

  return (
    <Flex flexDirection="column" w="80%">
      <DropdownOptions />
      <TooltipPopup />
      <HeaderContainer w="100%">
        <Flex flexDirection="column">
          {selectedProduct && (
            <Text bold xxl>
              {!selectedProduct.price || userHasTrialed
                ? t("pages:completeSignUpAddPaymentPage:headerBasic")
                : t("pages:completeSignUpAddPaymentPage:headerPro", {
                    countOfDays: selectedProduct.trial,
                  })}
            </Text>
          )}
          {!selectedProduct?.price && (
            <Text color={theme.colors.n80}>
              {t("pages:completeSignUpAddPaymentPage:subheader")}
            </Text>
          )}
        </Flex>
      </HeaderContainer>
      <Card p={6} w="100%" mt={4}>
        <SubscriptionsCard
          activeSubscription={selectedProduct}
          onSubscriptionChange={setSelectedProduct}
        />
      </Card>
      {selectedProduct?.price !== 0 && (
        <Card p={6} w="100%" mt={4} flexDirection="column">
          <PaymentMethodsSection />
        </Card>
      )}
      <Card p={6} w="100%" mt={4} flexDirection="column">
        {loadingPlans ? (
          <Flex justifyContent="center" alignItems="center" w="100%">
            <Spinner />
          </Flex>
        ) : (
          <>
            {selectedProduct &&
              (changeType === "UPGRADE" ||
                changeType === "MONTH" ||
                changeType === "SIXMONTH") && (
                <>
                  <OrderSummaryCard
                    setCoupon={setCoupon}
                    selectedProduct={selectedProduct}
                    withTrial={!!userCanTrial}
                  />
                  <Button
                    isLoading={
                      isCreateSubscriptionLoading ||
                      isChangeSubscriptionLoading ||
                      stripeLoading
                    }
                    disabled={
                      isCreateSubscriptionLoading ||
                      isChangeSubscriptionLoading ||
                      stripeLoading
                    }
                    onClick={() => {
                      if (selectedProduct?.price === 0) onSubmit();
                      else {
                        if (account?.stripeDefaultPaymentMethodId) onSubmit();
                        else
                          toast.error(
                            t(
                              "common:brokerPortalServerErrors:PAYMENT_METHOD_NEEDED"
                            ) || ""
                          );
                      }
                    }}
                    mt={8}
                    w="100%">
                    {t("pages:completeSignUpPickSubscription.subscribe")}
                  </Button>
                </>
              )}

            {changeType === "DOWNGRADE" && (
              <>
                <p>Please visit your billing page to downgrade plans.</p>
                <Button
                  mt={4}
                  onClick={() => {
                    navigate(
                      `${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`
                    );
                  }}>
                  Manage your subscription
                </Button>
              </>
            )}

            {changeType === "UNCHANGED" && <p>You are already on this plan.</p>}
          </>
        )}
      </Card>
      <Flex mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {errorSlug}
        </Form.Feedback>
      </Flex>
    </Flex>
  );
}

export default AddPaymentPage;
